import * as dateFns from 'date-fns';
import { HistoricVesselPoint } from '../../maritime-menu-options/history-panel/historic-vessel-point.model';
import type { LocationMessage } from '../../models/vessels/internal-ais-api.model';
import { Vessel, VesselSource } from '../../models/vessels/vessel.model';
import { HistoricVesselData } from '../vessels';

import { wrapRequest } from '../base';

/**
 * Convert a flat array of `LocationMessage` records (from the AIS API websocket)
 * into the `HistoricVesselData[]` shape consumed by the history panel rendering pipeline.
 *
 * Messages without a position (no latitude/longitude) are silently dropped.
 * The last positioned message for each vessel is used to populate the `vessel` summary.
 */
export const locationMessagesToHistoricVesselData = (
  messages: LocationMessage[]
): HistoricVesselData[] => {
  /**
   * Preserve prepend-merge semantics by ordering newest first.
   */
  const compareMessagesNewestFirst = (a: LocationMessage, b: LocationMessage) =>
    b.posDt - a.posDt;

  const hasValidPosition = (
    msg: LocationMessage
  ): msg is LocationMessage & { latitude: number; longitude: number } =>
    typeof msg.latitude === 'number' &&
    Number.isFinite(msg.latitude) &&
    msg.latitude >= -90 &&
    msg.latitude <= 90 &&
    typeof msg.longitude === 'number' &&
    Number.isFinite(msg.longitude) &&
    msg.longitude >= -180 &&
    msg.longitude <= 180;

  const grouped = new Map<
    number,
    (LocationMessage & { latitude: number; longitude: number })[]
  >();
  messages.forEach((msg) => {
    if (!hasValidPosition(msg)) return;
    const existing = grouped.get(msg.vesselUid) ?? [];
    existing.push(msg);
    grouped.set(msg.vesselUid, existing);
  });

  return Array.from(grouped.entries()).map(([vesselUid, vesselMessages]) => {
    const sortedVesselMessages = [...vesselMessages].sort(
      compareMessagesNewestFirst
    );

    const points: HistoricVesselPoint[] = sortedVesselMessages.map((msg) => ({
      id: `${vesselUid}-${msg.posDt}`,
      vessel_id: String(vesselUid),
      name: msg.vesselName ?? '',
      imo: msg.imo,
      mmsi: msg.mmsi,
      latitude: msg.latitude,
      longitude: msg.longitude,
      heading: msg.heading,
      speed: msg.sog ?? 0,
      course: msg.cog ?? 0,
      rot: msg.rot ?? 0,
      maneuver: 0,
      collection_type: msg.posSrc ?? 'unknown',
      timestamp: msg.posDt * 1000,
      created_at: msg.posDt * 1000,
      source: VesselSource.AIS,
      callsign: msg.callsign ?? '',
      shiptype: msg.vesselType ?? '',
    }));

    // Create the 'vessel' representation using the latest message as this will have the most recent position and status
    const latestMessage = sortedVesselMessages[0];
    const vessel: Vessel = {
      vessel_id: String(vesselUid),
      source: VesselSource.AIS,
      // Vessel properties, semi-static
      name: latestMessage.vesselName ?? '',
      imo: latestMessage.imo,
      mmsi: latestMessage.mmsi,
      callsign: latestMessage.callsign ?? '',
      shiptype: latestMessage.vesselType ?? '',
      // Location/voyage information that will change often
      latitude: latestMessage.latitude,
      longitude: latestMessage.longitude,
      heading: latestMessage.heading,
      speed: latestMessage.sog ?? 0,
      course: latestMessage.cog ?? 0,
    };

    return { messages: points, vessel };
  });
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Dispatch the websocket/location_messages_v2 request with retries.
 * Returns true on success, false if all retries fail.
 * Implemented recursively to avoid awaiting inside a loop. 9324904
 */
const dispatchQuery = async (
  callerFeature: string,
  geometry: object | null,
  token: string,
  startDatetime: string | undefined,
  endDatetime: string | undefined,
  mmsis?: number[],
  imos?: number[],
  sampleRate?: string,
  maxAttempts: number = 5,
  initialDelayMs: number = 250
): Promise<boolean> => {
  const attempt = async (n: number, delay: number): Promise<boolean> => {
    try {
      await wrapRequest('post', 'north_standard', '/ais-proxy', {
        headers: { 'x-caller-feature': callerFeature },
        body: {
          endpoint: 'websocket/location_messages_v2',
          token,
          ...(geometry ? { geometry } : {}),
          start_datetime: startDatetime,
          end_datetime: endDatetime,
          ...(mmsis?.length ? { mmsis } : {}),
          ...(imos?.length ? { imos } : {}),
          ...(sampleRate ? { sample_interval: sampleRate } : {}),
        },
      });
      return true;
    } catch (err) {
      if (n >= maxAttempts) return false;
      await sleep(delay);
      return attempt(n + 1, delay * 2);
    }
  };

  return attempt(1, initialDelayMs);
};

export const intialiseAisApiWebsocket = async (
  geometry: object | null,
  token: string,
  callerFeature: string,
  hours?: number,
  startDate?: Date,
  endDate?: Date,
  mmsis?: number[],
  imos?: number[],
  sampleRate?: string
): Promise<{ dispatchQuery: () => Promise<boolean> }> => {
  let startDatetime: string | undefined;
  let endDatetime: string | undefined;

  if (hours) {
    startDatetime = dateFns.subHours(new Date(), hours).toISOString();
    endDatetime = new Date().toISOString();
  } else if (startDate && endDate) {
    startDatetime = startDate.toISOString();
    endDatetime = endDate.toISOString();
  }

  return {
    dispatchQuery: () =>
      dispatchQuery(
        callerFeature,
        geometry,
        token,
        startDatetime,
        endDatetime,
        mmsis,
        imos,
        sampleRate
      ),
  };
};

/**
 * Convert a `LocationMessage` from the AIS API to a `Vessel`.
 * Returns `null` when the message has no position (latitude/longitude absent).
 */
export const locationMessageToVessel = (
  msg: LocationMessage | null | undefined
): Vessel | null => {
  if (!msg || msg.latitude === undefined || msg.longitude === undefined) {
    return null;
  }

  return {
    vessel_id: String(msg.vesselUid),
    name: msg.vesselName ?? '',
    imo: msg.imo,
    mmsi: msg.mmsi,
    latitude: msg.latitude,
    longitude: msg.longitude,
    heading: msg.heading,
    speed: msg.sog,
    course: msg.cog ?? 0,
    callsign: msg.callsign ?? '',
    shiptype: msg.vesselType,
    timestamp: msg.posDt * 1000,
    source: VesselSource.AIS,
  };
};
